/* eslint-disable jsx-a11y/click-events-have-key-events */
import cx from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import { RICOS_FONT_SIZE_TYPE } from './../../../../ricos-content';
import type { ToolbarContextType } from './../../../../ricos-context';
import { withToolbarContext } from './../../../../ricos-context';
import { getIsModalOpen, useModalState } from './../../../../ricos-modals/modal-service-provider';
import type { PublicCommands, TFunction } from './../../../../ricos-types';
import { Decoration_Type } from './../../../../ricos-types';
import Tooltip from './../../../../wix-rich-content-common/lib/Tooltip';

import { DropdownArrowIcon } from '../../../icons';
import type { ToolbarItem } from '../../../ToolbarItemCreator';
import styles from './FontSizeButton-scss';

const MAX_FONT_SIZE = 900;
const MIN_FONT_SIZE = 1;

const onInputChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  setInputValue: React.Dispatch<React.SetStateAction<string | number>>,
  publicCommands: PublicCommands
) => {
  const { value } = e.target;
  const valueAsNumber = Number.parseInt(value);
  if (!valueAsNumber && value !== '') return;
  setInputValue(value);
  const fontSize = Math.min(Math.max(MIN_FONT_SIZE, valueAsNumber), MAX_FONT_SIZE);
  publicCommands.insertDecoration(RICOS_FONT_SIZE_TYPE, { fontSize: `${fontSize}` });
};

const FontSizeButton = ({
  toolbarItem,
  context,
  dataHook,
}: {
  toolbarItem: ToolbarItem;
  context: ToolbarContextType & { t: TFunction };
  dataHook: string;
}) => {
  const { t, getPublicCommands } = context || {};
  const inputRef = useRef<HTMLInputElement>(null);
  const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
  const [inputValue, setInputValue] = useState<number | string>('');
  const publicCommands = getPublicCommands?.();
  const isModalOpen = useModalState(getIsModalOpen(`${Decoration_Type.FONT_SIZE}.modal`));

  const selectedFontSize: string = toolbarItem.attributes.selectedFontSize;

  useEffect(() => {
    setInputValue(selectedFontSize);
  }, [selectedFontSize]);

  const disabled = toolbarItem.attributes.disabled;
  const tooltip = t(toolbarItem.presentation?.tooltip);
  return (
    <Tooltip key={tooltip} content={tooltip} tooltipOffset={{ x: 0, y: -8 }}>
      <div
        className={cx(styles.fontSizeModalButtonWrapper, isModalOpen ? styles.active : '', {
          [styles.disabled]: disabled,
        })}
        ref={setReferenceElement}
      >
        <div
          data-hook={dataHook}
          className={styles.fontSizeModalButton}
          role="button"
          onClick={() => toolbarItem.commands?.click({ referenceElement })}
          tabIndex={-1}
        >
          <input
            aria-label={t('FormattingToolbar_CustomFontSizeButton_Tooltip')}
            className={styles.fontSizeModalInputButton}
            required
            value={inputValue}
            onChange={e => onInputChange(e, setInputValue, publicCommands)}
            onClick={() => inputRef.current?.select()}
            ref={inputRef}
          />
          <DropdownArrowIcon />
        </div>
      </div>
    </Tooltip>
  );
};

export default withToolbarContext(FontSizeButton);
